/**
 * cardKit — mini-cards, labeled arrows, annotation rails: the subject-
 * notation foundation (CATALOG F4).
 *
 * Serves: periodic element card · reaction arrow (conditions above/below) ·
 * labeled vector arrow · measurement chip · timeline event flag · ruby
 * annotation (rails share the above/below math).
 *
 * Pure layout math only — see kits/specs.ts. Local origin (0,0 = top-left)
 * for cards; arrows/rails anchor on caller coordinates like calloutKit.
 */
import type { KitBlock, LineSpec, PathSpec, PrimitiveSpec, RectSpec, TextSpec } from './specs';
import { TEXT_BOX_HEIGHT } from './specs';
import type { Point } from './calloutKit';
import { arrowHeadPath } from './calloutKit';

// ---------------------------------------------------------------------------
// Mini card
// ---------------------------------------------------------------------------

export interface MiniCardSlot {
  text: string;
  /** Dblclick edit tag (recipe param name). */
  param?: string;
  color?: string;
  fontWeight?: string | number;
}

export interface MiniCardOptions {
  width: number;
  height: number;
  /** Small line across the top band (element name, category, date…). */
  title?: MiniCardSlot;
  /** The big centered value (element symbol, the measurement, the note). */
  value?: MiniCardSlot;
  /** Small line in the bottom band (atomic mass, unit, sub-label). */
  caption?: MiniCardSlot;
  /** Small corner figure, top-left (atomic number, step index). */
  corner?: MiniCardSlot;
  fontFamily: string;
  /** Card ink for slots without their own color. */
  color: string;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  cornerRadius?: number;
}

/**
 * Mini-card frame with up to four typographic slots.
 *
 * THE CARD BAND LAW (element-card proportions, theme-agnostic):
 *   corner  — top-left, size h*0.14, inset h*0.07
 *   title   — centered in the top band at y = h*0.16, size h*0.13
 *   value   — centered at y = h*0.52, size h*0.42 (the card's reason to exist)
 *   caption — centered at y = h*0.88, size h*0.115
 * All slots are optional; absent slots emit nothing.
 */
export function miniCard(opts: MiniCardOptions): KitBlock {
  const { width: w, height: h } = opts;
  const specs: PrimitiveSpec[] = [];
  const frame: RectSpec = {
    kind: 'rect',
    x: 0,
    y: 0,
    width: w,
    height: h,
    fill: opts.fill ?? '',
    stroke: opts.stroke ?? opts.color,
    strokeWidth: opts.strokeWidth ?? Math.max(2, h * 0.02),
    rx: opts.cornerRadius ?? h * 0.06,
  };
  specs.push(frame);

  const slot = (
    s: MiniCardSlot,
    x: number,
    y: number,
    fontSize: number,
    originX: 'left' | 'center',
  ): TextSpec => ({
    kind: 'text',
    text: s.text,
    x,
    y,
    fontSize,
    fontFamily: opts.fontFamily,
    fill: s.color ?? opts.color,
    fontWeight: s.fontWeight ?? 'normal',
    originX,
    originY: 'center',
    ...(s.param ? { param: s.param } : {}),
  });

  if (opts.corner) specs.push(slot(opts.corner, h * 0.07, h * 0.07 + h * 0.07, h * 0.14, 'left'));
  if (opts.title) specs.push(slot(opts.title, w / 2, h * 0.16, h * 0.13, 'center'));
  if (opts.value) {
    specs.push(slot({ fontWeight: 'bold', ...opts.value }, w / 2, h * 0.52, h * 0.42, 'center'));
  }
  if (opts.caption) specs.push(slot(opts.caption, w / 2, h * 0.88, h * 0.115, 'center'));

  return { specs, width: w, height: h };
}

// ---------------------------------------------------------------------------
// Labeled arrow
// ---------------------------------------------------------------------------

export interface LabeledArrowOptions {
  from: Point;
  to: Point;
  color: string;
  strokeWidth: number;
  /** Default strokeWidth * 3, floor 8. */
  headSize?: number;
  /** Double-headed (⇌-style resonance arrows are M1's variant). */
  doubleHead?: boolean;
  label?: {
    text: string;
    fontSize: number;
    fontFamily: string;
    color?: string;
    param?: string;
    /** Perpendicular offset from the shaft (+ = above). Default fontSize*0.75. */
    offset?: number;
  };
}

export interface LabeledArrowBlock extends KitBlock {
  /** Shaft angle in degrees (fabric convention) BEFORE the readability flip. */
  shaftAngle: number;
  /** The angle actually given to the label text. */
  labelAngle: number;
}

/**
 * Arrow with a label riding the shaft.
 *
 * THE RIDING LABEL LAW: the label centers on the shaft midpoint, offset
 * perpendicular to it, and rotates WITH the shaft — same angle — so the
 * annotation reads along the arrow at any direction. READABILITY FLIP: when
 * the shaft points into the left half-plane (|angle| > 90°) the label angle
 * flips 180° so the text is never upside-down (the offset flips with it,
 * keeping the label on the same visual side).
 */
export function labeledArrow(opts: LabeledArrowOptions): LabeledArrowBlock {
  const { from, to } = opts;
  const rad = Math.atan2(to.y - from.y, to.x - from.x);
  const deg = (rad * 180) / Math.PI;
  const headSize = opts.headSize ?? Math.max(8, opts.strokeWidth * 3);
  const back = headSize * 1.1;

  const specs: PrimitiveSpec[] = [];
  const shaft: LineSpec = {
    kind: 'line',
    x1: from.x + (opts.doubleHead ? Math.cos(rad) * back : 0),
    y1: from.y + (opts.doubleHead ? Math.sin(rad) * back : 0),
    x2: to.x - Math.cos(rad) * back,
    y2: to.y - Math.sin(rad) * back,
    stroke: opts.color,
    strokeWidth: opts.strokeWidth,
    lineCap: 'round',
  };
  specs.push(shaft);
  const head: PathSpec = {
    kind: 'path',
    path: arrowHeadPath(to.x, to.y, rad, headSize),
    fill: opts.color,
    stroke: opts.color,
    strokeWidth: 1,
    lineJoin: 'round',
  };
  specs.push(head);
  if (opts.doubleHead) {
    const tail: PathSpec = {
      kind: 'path',
      path: arrowHeadPath(from.x, from.y, rad + Math.PI, headSize),
      fill: opts.color,
      stroke: opts.color,
      strokeWidth: 1,
      lineJoin: 'round',
    };
    specs.push(tail);
  }

  let labelAngle = deg;
  if (opts.label) {
    const flip = deg > 90 || deg < -90;
    labelAngle = flip ? deg - 180 * Math.sign(deg) : deg;
    const offset = opts.label.offset ?? opts.label.fontSize * 0.75;
    // "Above the shaft": offset along the perpendicular of the (possibly
    // flipped) LABEL angle — (sin, -cos) is the up-normal of a left-to-right
    // run in screen coords, and deriving it from labelAngle means the flip
    // flips the normal with the text, so the label sits above the shaft
    // whichever way the arrow points (east AND west), and on the text's
    // top side for vertical runs.
    const labelRad = (labelAngle * Math.PI) / 180;
    const midX = (from.x + to.x) / 2 + Math.sin(labelRad) * offset;
    const midY = (from.y + to.y) / 2 - Math.cos(labelRad) * offset;
    const label: TextSpec = {
      kind: 'text',
      text: opts.label.text,
      x: midX,
      y: midY,
      fontSize: opts.label.fontSize,
      fontFamily: opts.label.fontFamily,
      fill: opts.label.color ?? opts.color,
      originX: 'center',
      originY: 'center',
      angle: labelAngle,
      ...(opts.label.param ? { param: opts.label.param } : {}),
    };
    specs.push(label);
  }

  const xs = [from.x, to.x];
  const ys = [from.y, to.y];
  return {
    specs,
    width: Math.max(...xs) - Math.min(...xs) + headSize * 2,
    height: Math.max(...ys) - Math.min(...ys) + headSize * 2,
    shaftAngle: deg,
    labelAngle,
  };
}

// ---------------------------------------------------------------------------
// Annotation rails
// ---------------------------------------------------------------------------

export interface AnnotationRailsOptions {
  /** The annotated span (an arrow shaft, a word's box) — x1..x2 at y. */
  x1: number;
  x2: number;
  y: number;
  /** Condition/ruby text above the span. */
  above?: { text: string; param?: string };
  /** Condition text below the span. */
  below?: { text: string; param?: string };
  fontSize: number;
  fontFamily: string;
  color: string;
  /** Clearance between the span line and each rail. Default fontSize * 0.35. */
  gap?: number;
}

export interface AnnotationRailsBlock extends KitBlock {
  /** y of the above text's box BOTTOM and below text's box TOP. */
  aboveBottom: number | null;
  belowTop: number | null;
}

/**
 * Above/below annotation rails — the reaction-arrow conditions ("heat",
 * "catalyst") and ruby-text (furigana/pronunciation) construction: small
 * centered texts riding just above and/or below a horizontal span.
 *
 * THE RAIL LAW: above-text hangs its box BOTTOM `gap` above y; below-text
 * hangs its box TOP `gap` below y — so both rails clear the span
 * symmetrically regardless of font size.
 */
export function annotationRails(opts: AnnotationRailsOptions): AnnotationRailsBlock {
  const gap = opts.gap ?? opts.fontSize * 0.35;
  const cx = (opts.x1 + opts.x2) / 2;
  const boxH = opts.fontSize * TEXT_BOX_HEIGHT;
  const specs: PrimitiveSpec[] = [];
  let aboveBottom: number | null = null;
  let belowTop: number | null = null;

  if (opts.above) {
    aboveBottom = opts.y - gap;
    const t: TextSpec = {
      kind: 'text',
      text: opts.above.text,
      x: cx,
      y: aboveBottom,
      fontSize: opts.fontSize,
      fontFamily: opts.fontFamily,
      fill: opts.color,
      originX: 'center',
      originY: 'bottom',
      ...(opts.above.param ? { param: opts.above.param } : {}),
    };
    specs.push(t);
  }
  if (opts.below) {
    belowTop = opts.y + gap;
    const t: TextSpec = {
      kind: 'text',
      text: opts.below.text,
      x: cx,
      y: belowTop,
      fontSize: opts.fontSize,
      fontFamily: opts.fontFamily,
      fill: opts.color,
      originX: 'center',
      originY: 'top',
      ...(opts.below.param ? { param: opts.below.param } : {}),
    };
    specs.push(t);
  }

  const top = aboveBottom !== null ? aboveBottom - boxH : opts.y;
  const bottom = belowTop !== null ? belowTop + boxH : opts.y;
  return {
    specs,
    width: Math.abs(opts.x2 - opts.x1),
    height: bottom - top,
    aboveBottom,
    belowTop,
  };
}
